/**
 * 출고요청 배지 색 규칙. 관리자(목록 그리드 요약 칸, 상세 영역, 등록·수정 폼)와 화주사 사이트(출고요청관리)가 같은 규칙을 쓴다.
 * - done(초록): 끝났거나 갖춰졌다. 완료, 출고, 통관, PDI 완료, 검증 OK
 * - doing(파랑): 연결된 WO·운송요청이 진행 중이다
 * - warn(노랑): 사람이 챙겨야 한다. 검증 ALERT와 같은 수준. 검증 주의, AOS 예상 대상
 * - danger(빨강): 문제다. 검증 ERROR와 같은 수준. 검증 오류, 미통관, 취소된 WO·운송요청, 일정 지연, AOS 고지 대상(확정. 검증은 ALERT지만 고지 의무가 생긴 것)
 * - none(회색): 그 밖의 모든 것. 예정, 출고 방식, 없음, 해당 없음, PDI 미완료, 출고요청 요청·취소
 *
 * 함수는 목록 DTO와 수정 DTO를 같이 받는다. (두 DTO의 필드 이름이 같다: outReqWorkOrderId, outReqDeliveryId 등)
 */
export const POO_BADGE = {
  none: 'bg-body-tertiary text-body-secondary border-secondary border-opacity-50',
  doing: 'bg-primary-subtle text-primary-emphasis border-primary-subtle',
  warn: 'bg-warning-subtle text-warning-emphasis border-warning-subtle',
  done: 'bg-success-subtle text-success-emphasis border-success-subtle',
  danger: 'bg-danger-subtle text-danger-emphasis border-danger-subtle',
};

/** 출고요청 진행 상태 색: 완료(초록) / 요청·취소(회색) */
export function pooStatusClass(status: string): string {
  return status === 'COMPLETED' ? POO_BADGE.done : POO_BADGE.none;
}

/** WO 요약 색: 진행 중(파랑) / 완료(초록) / 취소(빨강, 검증 W2 ERROR) / WO 없음(회색) */
export function pooWoSummaryClass(item: any): string {
  if (item?.outReqWorkOrderId) {
    if (item.outReqWorkOrderStatus === 'COMPLETED') return POO_BADGE.done;
    if (item.outReqWorkOrderStatus === 'CANCELED') return POO_BADGE.danger;
    return POO_BADGE.doing;
  }
  return POO_BADGE.none;
}

/** 운송 색 (목록 운송 칸, 폼 출고 상태 배지): 운송 진행 중(파랑) / 확정인데 운송요청 없음(노랑) / 출고(초록) / 운송 취소(빨강, 검증 D1 ERROR) / 미정·예정(회색) */
export function pooDlvrSummaryClass(item: any): string {
  if (item?.outReqDeliveryId) {
    if (item.outReqDeliveryStatus === 'OUTBOUND') return POO_BADGE.done;
    if (item.outReqDeliveryStatus === 'CANCELED') return POO_BADGE.danger;
    return POO_BADGE.doing;
  }
  return item?.outReqDlvrStatus === 'CONFIRMED' ? POO_BADGE.warn : POO_BADGE.none;
}

/** 검증 요약 색: 문제 없음(초록) / 주의(노랑) / 오류(빨강). OK(통과) 결과는 문제가 아니다 */
export function pooCheckSummaryClass(checkResults: any[] | null | undefined): string {
  const results = checkResults ?? [];
  if (results.some(r => r.type === 'ERROR')) return POO_BADGE.danger;
  return results.some(r => r.type === 'ALERT') ? POO_BADGE.warn : POO_BADGE.done;
}

/**
 * AOS 색: 확정 고지 대상(빨강) / 예상 고지 대상만(노랑) / 해당 없음(회색). 둘 다면 확정이다.
 * 목록 DTO와 폼의 vehicleInfo가 같은 이름(aosActualDscTarget, aosExpectedDscTarget)을 쓴다.
 */
export function pooAosClass(item: any): string {
  if (item?.aosActualDscTarget) return POO_BADGE.danger;
  return item?.aosExpectedDscTarget ? POO_BADGE.warn : POO_BADGE.none;
}

/**
 * AOS 글자 (배지가 아닌 글자): 확정 고지 대상(굵은 빨강) / 예상 고지 대상만(굵은 기본 글자색) / 해당 없음(회색).
 * 그리드에 색이 많아지지 않게 예상 대상은 색 없이 굵기로만 구분한다.
 */
export function pooAosTextClass(item: any): string {
  if (item?.aosActualDscTarget) return 'text-danger fw-bold';
  return item?.aosExpectedDscTarget ? 'fw-bold' : 'text-body-secondary';
}

/** 출고 상태 문구:미정 빈칸 / 예정 / 확정 (목록 출고요청 칸·상세 배지는 빈칸 대신 "미정") */
export function pooDlvrStatusText(item: any): string {
  switch (item?.outReqDlvrStatus) {
    case 'PLANNED': return '예정';
    case 'CONFIRMED': return '확정';
    default: return '';
  }
}
